import { useCallback, useContext, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { ButtonSize, ButtonVariant } from '@components/buttons/button'
import { SanityImageFragment } from '@data/sanity/queries/types/image'
import Photo from '@components/photo'
import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { useAddItemsToCart } from '@lib/cart/hooks'
import ProductPrice from '@blocks/product/product-price'
import ProductAddToCart from '@blocks/product/product-add-to-cart'
import { stickyCTAAnimation } from '@lib/animate'
import Icon from '@components/icon'

// The vertical scroll position (in pixels) at which the CTA becomes visible
const CTA_SHOW_SCROLL_OFFSET = 300
// The distance (in pixels) from the bottom of the page at which the CTA hides
const CTA_HIDE_BEFORE_FOOTER = 300

interface StickyCTAProps {
  hasProductPrice: boolean
  productPrice?: number
  product: SanityProductFragment
  productImage?: SanityImageFragment
  variantIds: number[]
  activeVariant: SanityProductVariantFragment
}

const StickyCTA = ({
  hasProductPrice,
  productPrice,
  variantIds,
  product,
  productImage,
}: StickyCTAProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const addItemsToCart = useAddItemsToCart()

  const [isVisible, setIsVisible] = useState(false)

  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => {
      const { scrollY, innerHeight } = window
      const documentHeight = document.documentElement.scrollHeight
      const isNearBottom =
        scrollY + innerHeight >= documentHeight - CTA_HIDE_BEFORE_FOOTER

      setIsVisible(scrollY > CTA_SHOW_SCROLL_OFFSET && !isNearBottom)
    })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const handleAddToCart = async () => {
    const isSuccessful = await addItemsToCart(
      variantIds.map((variantId) => ({
        id: variantId,
        quantity: 1,
        productId: product._id,
        productCategoryId: product.productCategoryId,
      }))
    )

    if (isSuccessful) {
      triggerAnalyticsEvent(AnalyticsEventName.AddToCart)
    }
  }

  return (
    <motion.div
      variants={stickyCTAAnimation}
      initial="hide"
      animate={isVisible ? 'show' : 'hide'}
      custom={window.innerWidth >= 768}
      className="
        fixed
        w-full
        bg-white
        z-50
        py-1
        border-divider
        border-t
        bottom-0
        left-0
        md:top-[var(--headerHeight)]
        md:bottom-auto
        md:border-t-0
        md:border-b
      "
    >
      <div className="container flex items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-4">
          {productImage && (
            <Photo
              image={productImage}
              width={65}
              height={65}
              className="rounded-md"
            />
          )}
          <div className="flex flex-col py-3">
            <span className="font-semibold text-sm">{product.title}</span>
            {hasProductPrice && (
              <ProductPrice
                size="sm"
                price={productPrice}
                showPrice
                showOldPrice
                showCurrency
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 py-3">
          <ProductAddToCart
            variant={ButtonVariant.FILLED}
            size={ButtonSize.SMALL}
            disabled={variantIds.length === 0}
            onClick={handleAddToCart}
          >
            <div className="flex items-center gap-1">
              <Icon
                id={`sticky-add-to-cart-icon-${product._id}`}
                name="Plus"
                size="1.1rem"
              />
              <Icon
                id={`sticky-add-to-cart-icon-${product._id}`}
                name="Cart"
                size="1rem"
              />
            </div>
          </ProductAddToCart>
        </div>
      </div>
    </motion.div>
  )
}

export default StickyCTA
